import {
  LLMStreamStage,
  STREAM_TYPEWRITER_SPEED_MS,
} from '@conversed/shared/src/services/llm/llm.type';
import parse from 'html-react-parser';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { FiClock, FiZap } from 'react-icons/fi';
import type { StreamingState } from '../../../types/ChatWindow';
import { StreamPhase } from '../../../types/ChatWindow';
import Avatar from '../../Avatar';
import { purifyAndLinkifyText } from '../messageHelper';

const SEARCHING_PHRASES = [
  'Thinking this through...',
  'Working on it...',
  'Pulling together an answer...',
  'Almost there...',
  'Processing your question...',
  'Finding the best answer...',
  'Preparing your response...',
  'Gathering my thoughts...',
  'Formulating a reply...',
  'One moment...',
  'Getting that ready for you...',
  'Considering your question...',
  'Putting the finishing touches...',
  'Let me think about that...',
  'Figuring out the best way to help...',
];

const GENERATING_PHRASES = [
  'Generating your response...',
  'Writing your answer...',
  'Composing a reply...',
  'Crafting your response...',
  'Putting together your answer...',
  'Polishing the details...',
  'Fine-tuning the response...',
  'Adding the finishing touches...',
  'Turning ideas into words...',
  'Bringing it all together...',
  'Sharpening the response...',
  'Making it click...',
  'Tying up the loose ends...',
  'Almost there...',
  'Wrapping things up...',
];

type IProps = {
  streamingMessage: StreamingState;
  messageBubbleColor: string;
  messageBubbleTextColor: string;
  streamingIndicatorColor: string;
  avatarImg: string;
  onComplete?: () => void;
  // Called on every character advance so the parent can keep the scroll pinned to the bottom.
  onProgress?: () => void;
};

// Matches a partially-typed tag at the end of the text: a trailing `<`, `</`,
// or an unterminated tag like `<ul` / `</li` / `<a href="x`. A lone `<`
// followed by a non-letter (e.g. "5 < 10") is not matched and stays visible.
const TRAILING_PARTIAL_TAG_PATTERN = /<\/?([a-zA-Z][^>]*)?$/;

// Hold back a partially-typed trailing tag while the typewriter is mid-tag.
// The HTML parser inside DOMPurify drops or escapes incomplete tags
// differently on every tick, which makes the rendered content flash as each
// tag streams in character by character.
export const stripTrailingPartialTag = (text: string): string => {
  return text.replace(TRAILING_PARTIAL_TAG_PATTERN, '');
};

const StreamingBubble: React.FC<IProps> = ({
  streamingMessage,
  messageBubbleColor,
  messageBubbleTextColor,
  streamingIndicatorColor,
  avatarImg,
  onComplete,
  onProgress,
}) => {
  // Pick one phrase per stream turn, stable across re-renders.
  const searchingPhraseRef = useRef(
    SEARCHING_PHRASES[Math.floor(Math.random() * SEARCHING_PHRASES.length)],
  );
  const generatingPhraseRef = useRef(
    GENERATING_PHRASES[Math.floor(Math.random() * GENERATING_PHRASES.length)],
  );

  // Always points to the latest accumulated text so the interval never reads stale state.
  const accumulatedRef = useRef('');
  accumulatedRef.current = streamingMessage.accumulatedText;

  const [displayedText, setDisplayedText] = useState('');

  // Typewriter: advance one character per tick until displayedText catches up
  // to accumulatedText. Never jumps — when completing is set the onComplete
  // effect below fires once the last character is typed.
  useEffect(() => {
    if (streamingMessage.phase !== StreamPhase.Streaming) {
      setDisplayedText('');
      return;
    }

    const interval = setInterval(() => {
      setDisplayedText((previous) => {
        const accumulated = accumulatedRef.current;
        if (previous.length >= accumulated.length) {
          return previous;
        }
        return accumulated.slice(0, previous.length + 1);
      });
    }, STREAM_TYPEWRITER_SPEED_MS);

    return () => {
      clearInterval(interval);
    };
  }, [streamingMessage.phase]);

  // Once the typewriter has caught up AND the parent has signaled completing,
  // notify the parent to perform the final message swap.
  useEffect(() => {
    const caughtUp =
      displayedText.length >= streamingMessage.accumulatedText.length;
    if (!caughtUp || !streamingMessage.completing) {
      return;
    }
    onComplete?.();
  }, [
    displayedText,
    streamingMessage.accumulatedText,
    streamingMessage.completing,
    onComplete,
  ]);

  // Notify the parent on every character advance so it can keep the scroll pinned.
  useEffect(() => {
    if (displayedText.length > 0) {
      onProgress?.();
    }
  }, [displayedText, onProgress]);

  if (streamingMessage.phase === StreamPhase.Thinking) {
    return (
      <div className='message-container'>
        <div className='message-row direction-out'>
          <div
            className='streaming-indicator'
            style={
              {
                '--streaming-color': streamingIndicatorColor,
              } as React.CSSProperties
            }>
            <Avatar avatarImg={avatarImg} />
            <span className='streaming-thinking-dots'>
              <span />
              <span />
              <span />
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Once streaming starts there is a short gap before the typewriter produces
  // visible content (first ticks, or a held-back partial tag). Keep showing
  // the generating indicator instead of flashing an empty bubble.
  const waitingForFirstContent =
    streamingMessage.phase === StreamPhase.Streaming &&
    stripTrailingPartialTag(displayedText).length === 0;

  if (streamingMessage.phase === StreamPhase.Status || waitingForFirstContent) {
    const isGenerating =
      waitingForFirstContent ||
      streamingMessage.stage === LLMStreamStage.Generating;

    return (
      <div className='message-container'>
        <div className='message-row direction-out'>
          <div
            className='streaming-indicator'
            style={
              {
                '--streaming-color': streamingIndicatorColor,
              } as React.CSSProperties
            }>
            <Avatar avatarImg={avatarImg} />
            <span className='streaming-status-indicator'>
              {isGenerating ? (
                <FiZap className='streaming-status-icon' />
              ) : (
                <FiClock className='streaming-status-icon' />
              )}
              <span>
                {isGenerating
                  ? generatingPhraseRef.current
                  : searchingPhraseRef.current}
              </span>
              <span className='streaming-thinking-dots'>
                <span />
                <span />
                <span />
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  }

  const isCaughtUp =
    displayedText.length >= streamingMessage.accumulatedText.length;
  const parsedText = parse(
    purifyAndLinkifyText(stripTrailingPartialTag(displayedText)),
  );

  return (
    <div className='message-container'>
      <div className='message-row direction-out'>
        <div
          className='message-content text direction-out'
          style={{
            backgroundColor: messageBubbleColor,
            color: messageBubbleTextColor,
          }}>
          <Avatar avatarImg={avatarImg} />
          <div className='word-break-style'>
            <span
              style={{
                opacity: isCaughtUp && streamingMessage.completing ? 1 : 0.6,
              }}>
              {parsedText}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamingBubble;
